import {
  MembershipTypes,
  OauthQuery,
  getUserJourneyTypeIndex,
} from '@r-and-a-shared-ui/auth-client';
import { ActionHandlerParams, FormStepProps } from '../types';
import {
  hasValue,
  isFunction,
  isNullOrUndefined,
  safeParseZod,
  setNumberInSessionStorage,
  getNumberFromSessionStorage,
  mapByNestedKeys,
  getQueryParamByKey,
  ensureValidUri,
  getSSOUrl,
  isValidDateString,
} from '@r-and-a-shared-ui/utils';
import { FieldValues, UseFormTrigger } from 'react-hook-form';
import z from 'zod';
import { getStepIndexKey, getVisibleStep } from './form-steps-helper';

export type SkippingStepConfig = {
  values?: any;
  steps?: FormStepProps[];
  setValue?: (...args: any[]) => any;
};
export const addToCurrentStep = (
  valueToAdd: number,
  stepIndexKey: string,
  skippingStepConfig?: SkippingStepConfig,
) => {
  const currentStep = getNumberFromSessionStorage(stepIndexKey);
  const newStep = currentStep + valueToAdd;

  const nextStepToShow = getVisibleStep(
    newStep,
    valueToAdd > 0 ? 1 : -1,
    skippingStepConfig,
  );

  setNumberInSessionStorage(stepIndexKey, nextStepToShow);
  window.dispatchEvent(new Event('storage'));
};

export const getFieldsCurrentStep = (
  steps: FormStepProps[] | undefined,
  stepIndexKey: string,
): string[] | undefined => {
  const currentStep = getNumberFromSessionStorage(stepIndexKey);
  return getFieldsByStepIndex(currentStep, steps);
};

export const getFieldsByStepIndex = (
  stepIndex: number,
  steps: FormStepProps[] | undefined,
): string[] | undefined => {
  if (isNullOrUndefined(steps) || steps.length <= stepIndex) {
    return undefined;
  }
  const fieldsNames: string[] = [];

  steps[stepIndex].fieldsData?.forEach(field => {
    fieldsNames.push(field?.name);
    if (field.subfields?.length) {
      field.subfields?.forEach(subfield => fieldsNames.push(subfield.name));
    }
  });

  return fieldsNames;
};

export const validate = async (
  trigger: UseFormTrigger<FieldValues> | undefined,
  fields: string[] | undefined,
): Promise<boolean> => {
  if (!isFunction(trigger) || isNullOrUndefined(fields)) {
    return false;
  }
  const isValid = await trigger(fields, { shouldFocus: true });
  return isValid;
};

export const validateForm = async (
  params: ActionHandlerParams,
): Promise<boolean> => {
  const { trigger, formContentProps } = params;

  const stepIndexKey = getStepIndexKey(formContentProps?.formCodename);
  const isValid = await validate(
    trigger,
    getFieldsCurrentStep(formContentProps?.steps, stepIndexKey),
  );

  return isValid;
};
export const skipValidationForm = (params: ActionHandlerParams) => {
  const { initialFormState, formContentProps, setValue, clearErrors } = params;

  const stepIndexKey = getStepIndexKey(formContentProps?.formCodename);
  const currentStepFields = getFieldsCurrentStep(
    formContentProps?.steps,
    stepIndexKey,
  );

  setCurrentStepValuesToDefault(
    currentStepFields,
    initialFormState,
    setValue,
    clearErrors,
  );
};

export const setCurrentStepValuesToDefault = (
  currentStepFields: string[] | undefined,
  initialFormState?: any,
  setValue?: any,
  clearErrors?: any,
) => {
  if (isNullOrUndefined(currentStepFields)) {
    return;
  }

  for (let i = 0; i < currentStepFields.length; i++) {
    setValue(currentStepFields[i], initialFormState[currentStepFields[i]]);
    clearErrors(currentStepFields[i]);
  }
};

const setConfigParamsOnSubmitData = (submitData: any) => {
  if (isNullOrUndefined(submitData)) {
    return;
  }
  submitData.clientId = getQueryParamByKey(OauthQuery.CLIENT_ID);
  submitData.userJourneyType = getUserJourneyTypeIndex(
    getQueryParamByKey(OauthQuery.USER_JOURNEY_TYPE),
  );
  if (
    submitData.user_becameMasterCard &&
    !isValidDateString(submitData.user_becameMasterCard)
  ) {
    submitData.user_becameMasterCard = new Date().toDateString();
    submitData.user_membership = `${MembershipTypes.OneClub};${MembershipTypes.OneClubAdvantage}`;
  }
};

export const preExecMutationCheckWithFormValidation = async <
  Type extends z.ZodSchema,
>(
  params: ActionHandlerParams,
  schema: Type,
  defaultValuesObj?: unknown,
): Promise<z.TypeOf<Type> | undefined> => {
  const isValid = await validateForm(params);

  if (!isValid) {
    return undefined;
  }

  return preExecMutationCheck(params, schema, defaultValuesObj);
};

export const preExecMutationCheck = async <Type extends z.ZodSchema>(
  params: ActionHandlerParams,
  schema: Type,
  defaultValuesObj?: unknown,
): Promise<z.TypeOf<Type> | undefined> => {
  const { resource, submitData, bearerToken } = params;

  let objectToSubmit: any = mapByNestedKeys(submitData, defaultValuesObj, '_');

  setConfigParamsOnSubmitData(submitData); // this should not be called for pp forms

  if (!hasValue(resource)) {
    return safeParseZod(schema, objectToSubmit);
  }

  objectToSubmit = {
    callBackUrl: ensureValidUri(resource, getSSOUrl()),
    data: mapByNestedKeys(submitData, defaultValuesObj, '_'),
  };

  if (bearerToken) {
    objectToSubmit.bearerToken = bearerToken;
  }
  return safeParseZod(schema, objectToSubmit);
};

export const playAudioFromStep = (
  steps: FormStepProps[] | undefined,
  stepIndexKey: string,
) => {
  if (isNullOrUndefined(steps)) {
    return;
  }
  const currentStep = getNumberFromSessionStorage(stepIndexKey);
  const audioProps = steps[currentStep - 1]?.audio;
  if (isNullOrUndefined(audioProps)) {
    return;
  }
  const audio = new Audio(audioProps.url);
  audio.play();
};

export const checkAllFieldsHaveValues = (
  fieldNames: string[] | undefined,
  submitData: FieldValues,
) => {
  if (isNullOrUndefined(fieldNames)) {
    return true;
  }

  for (let i = 0; i < fieldNames.length; i++) {
    if (!submitData[fieldNames[i]]) {
      return false;
    }
  }
  return true;
};

export const scrollToErrorField = () => {
  try {
    const errorEl =
      (document.querySelector(
        '[class*="form-element__has-error"]',
      ) as HTMLElement | null) ?? null;

    const fieldEl = errorEl?.closest(
      '[class*="form-element"]',
    ) as HTMLElement | null;

    (fieldEl ?? errorEl)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    return;
  } catch (e) {
    console.error('Error scrolling to error field', e);
  }
};
